import React, { useState, useEffect } from 'react';

const steps = [
  { icon: '🧠', text: 'Understanding your prompt...' },
  { icon: '🎨', text: 'Composing the visual concept...' },
  { icon: '✏️', text: 'Sketching shapes and colors...' },
  { icon: '🖌️', text: 'Painting the details...' },
  { icon: '✨', text: 'Adding the final touches...' },
  { icon: '📦', text: 'Preparing your design...' },
];

function GeneratingState({ prompt }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stepVisible, setStepVisible] = useState(true);

  useEffect(() => {
    // Progress bar: fill to ~90% over 18 seconds, then stall
    const duration = 18000;
    const interval = 100;
    const increment = 90 / (duration / interval);
    let current = 0;

    const progressTimer = setInterval(() => {
      current = Math.min(current + increment, 90);
      setProgress(current);
    }, interval);

    // Cycle through steps every 3 seconds with fade transition
    const stepTimer = setInterval(() => {
      setStepVisible(false);
      setTimeout(() => {
        setStepIndex(i => Math.min(i + 1, steps.length - 1));
        setStepVisible(true);
      }, 300);
    }, 3000);

    return () => {
      clearInterval(progressTimer);
      clearInterval(stepTimer);
    };
  }, []);

  const current = steps[stepIndex];

  // Phone dimensions (same as PhoneMockup)
  const phoneW = 320;
  const phoneH = 640;
  const bezel = 14;
  const radius = 46;
  const screenRadius = 32;
  const screenX = bezel;
  const screenY = bezel;
  const screenW = phoneW - bezel * 2;
  const screenH = phoneH - bezel * 2;
  const islandW = 100;
  const islandH = 28;
  const islandX = phoneW / 2 - islandW / 2;
  const islandY = screenY + 12;

  // Shimmer gradient sweep range in SVG user units
  const shimmerFrom = `${-screenW} 0`;
  const shimmerTo = `${screenW * 2} 0`;

  return (
    <div className="generating-state">
      <div className="skeleton-phone-wrapper">
        <svg
          viewBox={`0 0 ${phoneW} ${phoneH}`}
          xmlns="http://www.w3.org/2000/svg"
          className="skeleton-phone-svg"
        >
          <defs>
            <linearGradient id="sk-body-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2a2a2e" />
              <stop offset="100%" stopColor="#18181b" />
            </linearGradient>

            {/* Shimmer sweep gradient — x1/x2 in userSpaceOnUse for reliable SVG animation */}
            <linearGradient
              id="sk-shimmer"
              x1={screenX - screenW}
              y1="0"
              x2={screenX}
              y2="0"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%"   stopColor="#ffffff" stopOpacity="0" />
              <stop offset="40%"  stopColor="#ffffff" stopOpacity="0.06" />
              <stop offset="60%"  stopColor="#ffffff" stopOpacity="0.13" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                from={shimmerFrom}
                to={shimmerTo}
                dur="1.8s"
                repeatCount="indefinite"
              />
            </linearGradient>

            {/* Accent glow for the screen edge while generating */}
            <linearGradient id="sk-glow-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a855f7" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.2" />
            </linearGradient>

            <clipPath id="sk-screen-clip">
              <rect x={screenX} y={screenY} width={screenW} height={screenH} rx={screenRadius} ry={screenRadius} />
            </clipPath>

            <filter id="sk-shadow" x="-15%" y="-8%" width="130%" height="120%">
              <feDropShadow dx="0" dy="14" stdDeviation="20" floodColor="#000" floodOpacity="0.65" />
            </filter>

            <filter id="sk-glow-filter" x="-10%" y="-5%" width="120%" height="115%">
              <feGaussianBlur stdDeviation="6" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          {/* Body */}
          <rect x="0" y="0" width={phoneW} height={phoneH} rx={radius} ry={radius}
            fill="url(#sk-body-grad)" filter="url(#sk-shadow)" />

          {/* Edge ring */}
          <rect x="0.5" y="0.5" width={phoneW - 1} height={phoneH - 1} rx={radius} ry={radius}
            fill="none" stroke="#3a3a3c" strokeWidth="1.5" />

          {/* Pulsing accent glow on screen border */}
          <rect x={screenX} y={screenY} width={screenW} height={screenH} rx={screenRadius} ry={screenRadius}
            fill="none" stroke="url(#sk-glow-grad)" strokeWidth="2" className="sk-glow-ring" />

          {/* Side buttons */}
          <rect x="-3" y="160" width="4" height="46" rx="2" fill="#2a2a2c" />
          <rect x="-3" y="220" width="4" height="46" rx="2" fill="#2a2a2c" />
          <rect x="-3" y="112" width="4" height="30" rx="2" fill="#2a2a2c" />
          <rect x={phoneW - 1} y="190" width="4" height="64" rx="2" fill="#2a2a2c" />

          {/* Screen background */}
          <rect x={screenX} y={screenY} width={screenW} height={screenH} rx={screenRadius} ry={screenRadius}
            fill="#111113" />

          {/* Skeleton content blocks inside screen */}
          <g clipPath="url(#sk-screen-clip)">
            {/* Large art area */}
            <rect x={screenX + 10} y={screenY + 60} width={screenW - 20} height={screenH * 0.5} rx="8"
              fill="#252529" className="sk-pulse-1" />
            {/* Sub-block 2 — title line */}
            <rect x={screenX + 10} y={screenY + 60 + screenH * 0.5 + 14} width={screenW * 0.65} height={20} rx="6"
              fill="#252529" className="sk-pulse-2" />
            {/* Sub-block 3 — sub-line */}
            <rect x={screenX + 10} y={screenY + 60 + screenH * 0.5 + 44} width={screenW * 0.42} height={14} rx="6"
              fill="#1e1e22" className="sk-pulse-3" />
            {/* Sub-block 4 — tag chip */}
            <rect x={screenX + 10} y={screenY + 60 + screenH * 0.5 + 72} width={screenW * 0.28} height={12} rx="6"
              fill="#1e1e22" className="sk-pulse-3" />

            {/* Shimmer sweep over entire screen */}
            <rect
              x={screenX} y={screenY} width={screenW} height={screenH}
              fill="url(#sk-shimmer)"
            />
          </g>

          {/* Dynamic Island */}
          <rect x={islandX} y={islandY} width={islandW} height={islandH}
            rx={islandH / 2} ry={islandH / 2} fill="#000" />
          <circle cx={islandX + islandW - 22} cy={islandY + islandH / 2} r="7" fill="#1a1a1a" />
          <circle cx={islandX + islandW - 22} cy={islandY + islandH / 2} r="3.5" fill="#0d0d0d" />
        </svg>

        {/* Overlay: step text centered over the skeleton phone */}
        <div className="skeleton-phone-overlay">
          <div className="generating-icon">{current.icon}</div>
          {prompt && (
            <div className="generating-prompt">"{prompt}"</div>
          )}
          <p className={`generating-step ${stepVisible ? 'step-visible' : 'step-hidden'}`}>
            {current.text}
          </p>
          <div className="generating-progress-track">
            <div
              className="generating-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="generating-dots">
            {steps.map((_, i) => (
              <div key={i} className={`generating-dot ${i <= stepIndex ? 'active' : ''}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneratingState;
